import {
	BANNER_VIEWS_CTA_MAX,
	BANNER_VIEWS_NOTE_MAX_LENGTH,
	BANNER_VIEWS_SCHEMA_VERSION,
	type BannerCtaEvent,
	type BannerDismissSource,
	type BannerRecord,
	type BannerScope,
	type BannerSurfaceState,
	type BannerViewsDocument,
} from 'src/types/banner-views';

/** Lowercase start; camelCase / kebab / snake allowed (e.g. roomsList, energy-dashboard). */
const SLUG_RE = /^[a-z0-9][a-zA-Z0-9-_]*$/;
const DISMISS_SOURCES: readonly BannerDismissSource[] = ['user', 'cta', 'auto'];
const SCOPES: readonly BannerScope[] = ['shared', 'per-surface'];

const KNOWN_RECORD_KEYS = new Set([
	'schemaVersion',
	'id',
	'scope',
	'note',
	'firstSeenAt',
	'lastSeenAt',
	'impressionCount',
	'dismissedAt',
	'lastDismissedAt',
	'dismissCount',
	'dismissSource',
	'snoozeUntil',
	'convertedAt',
	'ctas',
	'lastCtaId',
	'lastCtaAt',
	'surfaces',
	'extras',
]);

const KNOWN_SURFACE_KEYS = new Set([
	'firstSeenAt',
	'lastSeenAt',
	'impressionCount',
	'dismissedAt',
	'lastDismissedAt',
	'dismissCount',
	'dismissSource',
	'snoozeUntil',
	'ctas',
	'lastCtaId',
	'lastCtaAt',
	'note',
	'extras',
]);

export const isValidBannerSlug = (value: unknown): value is string =>
	typeof value === 'string' && value.length > 0 && SLUG_RE.test(value);

const isFiniteNonNeg = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value) && value >= 0;

const isNonNegInt = (value: unknown): value is number =>
	isFiniteNonNeg(value) && Number.isInteger(value);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const sanitizeNote = (value: unknown, errors: string[], path: string): string | undefined => {
	if (value === undefined) return undefined;
	if (typeof value !== 'string') {
		errors.push(`${path}: note must be a string`);
		return undefined;
	}
	if (value.length > BANNER_VIEWS_NOTE_MAX_LENGTH) {
		errors.push(`${path}: note exceeds ${BANNER_VIEWS_NOTE_MAX_LENGTH} chars`);
		return value.slice(0, BANNER_VIEWS_NOTE_MAX_LENGTH);
	}
	return value;
};

const sanitizeExtras = (
	value: unknown,
	errors: string[],
	path: string,
): Record<string, unknown> | undefined => {
	if (value === undefined) return undefined;
	if (!isPlainObject(value)) {
		errors.push(`${path}: extras must be a plain object`);
		return undefined;
	}
	try {
		return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
	} catch {
		errors.push(`${path}: extras must be JSON-serializable`);
		return undefined;
	}
};

const sanitizeDismissSource = (
	value: unknown,
	errors: string[],
	path: string,
): BannerDismissSource | undefined => {
	if (value === undefined) return undefined;
	if (typeof value === 'string' && (DISMISS_SOURCES as readonly string[]).includes(value)) {
		return value as BannerDismissSource;
	}
	errors.push(`${path}: invalid dismissSource`);
	return undefined;
};

const sanitizeCtas = (value: unknown, errors: string[], path: string): BannerCtaEvent[] | undefined => {
	if (value === undefined) return undefined;
	if (!Array.isArray(value)) {
		errors.push(`${path}: ctas must be an array`);
		return undefined;
	}

	const out: BannerCtaEvent[] = [];
	for (let i = 0; i < value.length; i++) {
		const item = value[i];
		if (!isPlainObject(item)) {
			errors.push(`${path}[${i}]: must be an object`);
			continue;
		}
		if (!isValidBannerSlug(item.id) && !(typeof item.id === 'string' && item.id.length > 0)) {
			errors.push(`${path}[${i}].id: invalid`);
			continue;
		}
		if (!isFiniteNonNeg(item.at)) {
			errors.push(`${path}[${i}].at: invalid timestamp`);
			continue;
		}
		const event: BannerCtaEvent = {
			id: String(item.id),
			at: item.at,
		};
		if (typeof item.surface === 'string' && item.surface.length > 0) {
			event.surface = item.surface;
		}
		if (typeof item.href === 'string') {
			event.href = item.href;
		}
		out.push(event);
	}

	return out.slice(-BANNER_VIEWS_CTA_MAX);
};

const pickPassthrough = (
	source: Record<string, unknown>,
	known: Set<string>,
): Record<string, unknown> => {
	const extra: Record<string, unknown> = {};
	for (const [key, val] of Object.entries(source)) {
		if (!known.has(key)) {
			extra[key] = val;
		}
	}
	return extra;
};

export const sanitizeBannerSurfaceState = (
	raw: unknown,
	path: string,
	errors: string[],
): BannerSurfaceState | null => {
	if (!isPlainObject(raw)) {
		errors.push(`${path}: must be an object`);
		return null;
	}

	const state: BannerSurfaceState = {
		...pickPassthrough(raw, KNOWN_SURFACE_KEYS),
	};

	const assignTs = (key: keyof BannerSurfaceState) => {
		if (raw[key as string] === undefined) return;
		if (isFiniteNonNeg(raw[key as string])) {
			(state as Record<string, unknown>)[key as string] = raw[key as string];
		} else {
			errors.push(`${path}.${String(key)}: invalid timestamp`);
		}
	};
	const assignCount = (key: keyof BannerSurfaceState) => {
		if (raw[key as string] === undefined) return;
		if (isNonNegInt(raw[key as string])) {
			(state as Record<string, unknown>)[key as string] = raw[key as string];
		} else {
			errors.push(`${path}.${String(key)}: invalid count`);
		}
	};

	assignTs('firstSeenAt');
	assignTs('lastSeenAt');
	assignCount('impressionCount');
	assignTs('dismissedAt');
	assignTs('lastDismissedAt');
	assignCount('dismissCount');
	assignTs('snoozeUntil');
	assignTs('lastCtaAt');

	const dismissSource = sanitizeDismissSource(raw.dismissSource, errors, `${path}.dismissSource`);
	if (dismissSource) state.dismissSource = dismissSource;

	if (typeof raw.lastCtaId === 'string') state.lastCtaId = raw.lastCtaId;

	const note = sanitizeNote(raw.note, errors, path);
	if (note !== undefined) state.note = note;

	const extras = sanitizeExtras(raw.extras, errors, `${path}.extras`);
	if (extras) state.extras = extras;

	const ctas = sanitizeCtas(raw.ctas, errors, `${path}.ctas`);
	if (ctas) state.ctas = ctas;

	return state;
};

export const sanitizeBannerRecord = (
	raw: unknown,
	expectedId: string,
	errors: string[],
): BannerRecord | null => {
	if (!isPlainObject(raw)) {
		errors.push(`banners.${expectedId}: must be an object`);
		return null;
	}

	if (!isValidBannerSlug(expectedId)) {
		errors.push(`banners.${expectedId}: invalid id slug`);
		return null;
	}

	const id = typeof raw.id === 'string' ? raw.id : expectedId;
	if (id !== expectedId) {
		errors.push(`banners.${expectedId}: id mismatch`);
		return null;
	}
	if (!isValidBannerSlug(id)) {
		errors.push(`banners.${expectedId}: invalid id`);
		return null;
	}

	if (raw.schemaVersion !== BANNER_VIEWS_SCHEMA_VERSION) {
		errors.push(`banners.${expectedId}: unsupported schemaVersion`);
		return null;
	}

	if (typeof raw.scope !== 'string' || !(SCOPES as readonly string[]).includes(raw.scope)) {
		errors.push(`banners.${expectedId}: invalid scope`);
		return null;
	}

	const record: BannerRecord = {
		...pickPassthrough(raw, KNOWN_RECORD_KEYS),
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		id,
		scope: raw.scope as BannerScope,
	};

	const assignTs = (key: keyof BannerRecord) => {
		if (raw[key as string] === undefined) return;
		if (isFiniteNonNeg(raw[key as string])) {
			(record as Record<string, unknown>)[key as string] = raw[key as string];
		} else {
			errors.push(`banners.${expectedId}.${String(key)}: invalid timestamp`);
		}
	};
	const assignCount = (key: keyof BannerRecord) => {
		if (raw[key as string] === undefined) return;
		if (isNonNegInt(raw[key as string])) {
			(record as Record<string, unknown>)[key as string] = raw[key as string];
		} else {
			errors.push(`banners.${expectedId}.${String(key)}: invalid count`);
		}
	};

	assignTs('firstSeenAt');
	assignTs('lastSeenAt');
	assignCount('impressionCount');
	assignTs('dismissedAt');
	assignTs('lastDismissedAt');
	assignCount('dismissCount');
	assignTs('snoozeUntil');
	assignTs('convertedAt');
	assignTs('lastCtaAt');

	const dismissSource = sanitizeDismissSource(
		raw.dismissSource,
		errors,
		`banners.${expectedId}.dismissSource`,
	);
	if (dismissSource) record.dismissSource = dismissSource;

	if (typeof raw.lastCtaId === 'string') record.lastCtaId = raw.lastCtaId;

	const note = sanitizeNote(raw.note, errors, `banners.${expectedId}`);
	if (note !== undefined) record.note = note;

	const extras = sanitizeExtras(raw.extras, errors, `banners.${expectedId}.extras`);
	if (extras) record.extras = extras;

	const ctas = sanitizeCtas(raw.ctas, errors, `banners.${expectedId}.ctas`);
	if (ctas) record.ctas = ctas;

	if (raw.surfaces !== undefined) {
		if (!isPlainObject(raw.surfaces)) {
			errors.push(`banners.${expectedId}.surfaces: must be an object`);
		} else {
			const surfaces: Record<string, BannerSurfaceState> = {};
			for (const [surfaceId, surfaceRaw] of Object.entries(raw.surfaces)) {
				if (!isValidBannerSlug(surfaceId)) {
					errors.push(`banners.${expectedId}.surfaces.${surfaceId}: invalid surface id`);
					continue;
				}
				const surface = sanitizeBannerSurfaceState(
					surfaceRaw,
					`banners.${expectedId}.surfaces.${surfaceId}`,
					errors,
				);
				if (surface) surfaces[surfaceId] = surface;
			}
			record.surfaces = surfaces;
		}
	}

	return record;
};

/**
 * Sanitize + validate a banner_views document.
 * Returns a cleaned document or null if the top-level shape is unusable.
 * Soft field errors are collected; callers decide whether to persist.
 */
export const sanitizeBannerViewsDocument = (
	raw: unknown,
): { document: BannerViewsDocument | null; errors: string[] } => {
	const errors: string[] = [];

	if (!isPlainObject(raw)) {
		return { document: null, errors: ['banner_views: must be an object'] };
	}

	if (raw.schemaVersion !== BANNER_VIEWS_SCHEMA_VERSION) {
		return {
			document: null,
			errors: ['banner_views: unsupported schemaVersion'],
		};
	}

	if (!isPlainObject(raw.banners)) {
		return {
			document: null,
			errors: ['banner_views.banners: must be an object'],
		};
	}

	const banners: Record<string, BannerRecord> = {};
	for (const [id, recordRaw] of Object.entries(raw.banners)) {
		const record = sanitizeBannerRecord(recordRaw, id, errors);
		if (record) banners[id] = record;
	}

	return {
		document: {
			schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
			banners,
		},
		errors,
	};
};

/** Strict gate: document must sanitize with zero errors. */
export const validateBannerViewsDocument = (
	raw: unknown,
): { ok: true; document: BannerViewsDocument } | { ok: false; errors: string[] } => {
	const { document, errors } = sanitizeBannerViewsDocument(raw);
	if (!document || errors.length > 0) {
		return { ok: false, errors: errors.length ? errors : ['banner_views: invalid'] };
	}
	return { ok: true, document };
};

export const emptyBannerViewsDocument = (): BannerViewsDocument => ({
	schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
	banners: {},
});

/**
 * Deep-merge banner records. `patch` wins for overlapping keys;
 * surfaces are merged per surface id; ctas are replaced by patch when present.
 */
export const mergeBannerViewsDocuments = (
	base: BannerViewsDocument,
	patch: BannerViewsDocument,
): BannerViewsDocument => {
	const banners: Record<string, BannerRecord> = { ...base.banners };

	for (const [id, patchRecord] of Object.entries(patch.banners)) {
		const existing = banners[id];
		if (!existing) {
			banners[id] = patchRecord;
			continue;
		}

		const surfaces = {
			...(existing.surfaces || {}),
			...(patchRecord.surfaces || {}),
		};

		banners[id] = {
			...existing,
			...patchRecord,
			surfaces: Object.keys(surfaces).length ? surfaces : undefined,
		};
	}

	return {
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		banners,
	};
};
